import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { io, Socket } from "socket.io-client";
import { supabase } from "@/integrations/supabase/client";
import { apiClient } from "@/shared/services/api";

// Component imports
import { VideoCallToolbar } from "./components/VideoCallToolbar";
import { VideoStreams } from "./components/VideoStreams";
import { ChatPanel } from "./components/ChatPanel";
import { NotesPanel } from "./components/NotesPanel";
import { AnnotatePanel } from "./components/AnnotatePanel";
import { MeasurePanel } from "./components/MeasurePanel";
import { HostLeftModal } from "./components/HostLeftModal";
import { GuestMeasurementOverlay } from "./components/GuestMeasurementOverlay";
import { GuestARMeasurementOverlay } from "./components/GuestARMeasurementOverlay";
import { DownloadGuestAppModal } from "./components/DownloadGuestAppModal";
import { ReferenceObjectSelector } from "./components/ReferenceObjectSelector";
import { ReferenceCalibrationOverlay } from "./components/ReferenceCalibrationOverlay";
import { AISummaryModal } from "./components/AISummaryModal";
import { VideoCallToasts } from "@/features/videocall/components/components/Videocalltoasts";

// Hook imports
import { useWebRTC } from "./hooks/useWebRTC";
import { useMediaDevices } from "./hooks/useMediaDevices";
import { useRecording } from "./hooks/useRecording";
import { useAnnotations } from "./hooks/useAnnotations";
import { useChat } from "./hooks/useChat";
import { useMeasurement } from "./hooks/useMeasurement";
import { useRealtimeTranscription } from "./hooks/useRealtimeTranscription"; // ✅ ENABLED for AI summary
import { useTranslation } from "./hooks/useTranslation"; // ✅ Live voice translation (send)
import { useTranslationPlayback } from "./hooks/useTranslationPlayback"; // ✅ Live voice translation (receive)
import { useOrientationSync } from "./hooks/useOrientationSync"; // ✅ Device orientation sync for video rotation
import { useDeviceOrientation } from "./hooks/useDeviceOrientation"; // ✅ Device orientation for UI rotation
import { useScreenOrientation } from "@/shared/hooks/useScreenOrientation"; // ✅ Debounced orientation for recording
import { useWebViewViewportHeight } from "@/shared/hooks/useWebViewViewportHeight";

// Utility imports
import { detectDevice } from "@/utils/device-detection";

// Type imports
import type { VideoCallRoomProps } from "./types";

const USE_MEDIASOUP_SERVER_RECORDING =
  import.meta.env.VITE_USE_MEDIASOUP === "true" &&
  import.meta.env.VITE_MEDIASOUP_SERVER_RECORDING === "true";

export function VideoCallRoom({
  roomId,
  isHost,
  onEndCall,
  customerData,
  displayName,
  guestToken,
}: VideoCallRoomProps) {
  const location = useLocation() as any;
  const prejoinState =
    (location?.state && (location.state as any).prejoin) || undefined;

  // State - Initialize from prejoin state if available
  const [isMuted, setIsMuted] = useState(() => prejoinState?.micOn === false);
  const [isVideoOff, setIsVideoOff] = useState(() => prejoinState?.camOn === false);
  const [activePanel, setActivePanel] = useState<
    "chat" | "notes" | "annotate" | "measure" | null
  >(null);
  const [notes, setNotes] = useState("");
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [remoteConnected, setRemoteConnected] = useState(false);
  const [hostLeftCountdown, setHostLeftCountdown] = useState<number | null>(null);
  const [guestWaiting, setGuestWaiting] = useState(false);
  const [callTimeRemaining, setCallTimeRemaining] = useState<number | null>(null); // 30-minute timer in seconds
  const [isEndingCall, setIsEndingCall] = useState(false); // Prevent double end call
  const [rotationPromptDismissed, setRotationPromptDismissed] = useState(false);

  // Guest measurement state
  const [isGuestMeasuring, setIsGuestMeasuring] = useState(false);
  const [guestMeasurementPoints, setGuestMeasurementPoints] = useState<{x: number, y: number}[]>([]);
  const [currentMeasurementResult, setCurrentMeasurementResult] = useState<{
    real_distance_cm: number;
    real_distance_inches: number;
    confidence: number;
  } | null>(null);

  // Device selection state (for guests only)
  const [showDeviceSelection, setShowDeviceSelection] = useState(false);
  const [selectedDeviceModel, setSelectedDeviceModel] = useState<string | null>(null);

  // Reference calibration state
  const [showReferenceObjectSelector, setShowReferenceObjectSelector] = useState(false);
  const [isReferenceCalibrating, setIsReferenceCalibrating] = useState(false);
  const [referencePoints, setReferencePoints] = useState<{x: number, y: number}[]>([]);
  const [referenceObject, setReferenceObject] = useState<{ name: string; expectedLength: number } | null>(null);
  const [depthScale, setDepthScale] = useState<number | null>(null); // Scale factor to correct depth

  // AI Summary state
  const [showAISummary, setShowAISummary] = useState(false);

  // Live Translation state
  const [isTranslationEnabled, setIsTranslationEnabled] = useState(false); // host: controlled by toolbar
  const [isTranslationActive, setIsTranslationActive] = useState(false);   // both: driven by socket events
  const [hostLanguage, setHostLanguage] = useState('en');
  const [guestLanguage, setGuestLanguage] = useState('es');

  // Refs
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null); // ✅ For transcription of both parties
  const callStartRef = useRef<Date | null>(null);
  const peerRef = useRef<RTCPeerConnection | null>(null);

  const hostOrientationRef = useRef<number>(0);
  const remoteOrientationRef = useRef<number>(0);

  const participantName =
    displayName?.trim() || (isHost ? "Host" : "Guest");
  const SIGNALING_URL = import.meta.env.VITE_SIGNALING_SERVER_URL!;
  const BASE_URL = SIGNALING_URL;

  // ✅ REMOVED: Duplicate join event call
  // VideoCallPage.tsx already handles this - removing to prevent duplicate "host-joined"/"guest-joined" events

  // WebView viewport height hook (sets --app-vh CSS variable)
  useWebViewViewportHeight();

  // Black background for html/body so sidebars (pillarboxing) match video call UI
  useEffect(() => {
    const prevHtml = document.documentElement.style.backgroundColor;
    const prevBody = document.body.style.backgroundColor;
    document.documentElement.style.backgroundColor = '#000';
    document.body.style.backgroundColor = '#000';
    return () => {
      document.documentElement.style.backgroundColor = prevHtml;
      document.body.style.backgroundColor = prevBody;
    };
  }, []);

  // ✅ Device orientation hook - for rotating entire UI container in landscape
  const { rotationLockSuspected } = useDeviceOrientation();

  // ✅ Debounced orientation for recording (host only) - FaceTime-style stable state
  const { angle: hostOrientationAngle } = useScreenOrientation({
    enabled: isHost && !!roomId,
    debounceMs: 400,
  });
  useEffect(() => {
    hostOrientationRef.current = hostOrientationAngle;
  }, [hostOrientationAngle]);

  // Recording hook - must be before useWebRTC since it provides setIsRecording
  const { isRecording, setIsRecording, uploadProgress, startRecording, stopRecording, toggleRecording, emergencySave } =
    useRecording({
      roomId,
      isHost,
      socketRef,
      remoteVideoRef,
      localVideoRef,
      localStreamRef,
      baseUrl: BASE_URL,
      hostOrientationRef,
      remoteOrientationRef,
    });

  // ✅ FIX: Save recording before page refresh/unload
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Prevent navigation if recording OR upload is in progress
      const isUploadInProgress = uploadProgress !== null && uploadProgress < 100;

      console.log('[VideoCallRoom] beforeunload fired', {
        isRecording,
        uploadProgress,
        isUploadInProgress,
        willPrevent: isRecording || isUploadInProgress
      });

      if (isRecording || isUploadInProgress) {
        console.log('[VideoCallRoom] Recording/upload in progress, preventing navigation...', {
          isRecording,
          uploadProgress
        });

        // Show browser confirmation dialog
        // NOTE: We do NOT call stopRecording() here because:
        // - If user clicks "Cancel" → they want to continue recording
        // - If user clicks "Leave" → recording will be lost (expected behavior)
        const message = isUploadInProgress
          ? `Recording upload in progress (${uploadProgress}%). Please wait for upload to complete.`
          : 'Recording in progress. If you leave, your recording will be lost.';

        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    // Also handle pagehide event (more reliable on mobile/iOS)
    const handlePageHide = () => {
      if (isRecording) {
        console.log('[VideoCallRoom] Page hiding with recording in progress, triggering emergency save...');
        // ✅ FIX: Use emergencySave instead of stopRecording
        // emergencySave is synchronous and uses sendBeacon, so it works during page unload
        emergencySave();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [isRecording, uploadProgress, stopRecording, emergencySave]);

  // Custom hooks
  const {
    connectionStatus,
    remoteConnected: remoteConnectedFromHook,
    remoteVideoEnabled,
    waitingForHost,
    remoteVideoMuted,
    startCall,
    handleEndCall: webrtcEndCall,
    toggleMute,
    toggleVideo,
    handleTapToPlay,
    setRemoteAudioMuted,
    signalingServiceRef,
    webrtcServiceRef,
  } = useWebRTC({
    roomId,
    isHost,
    localVideoRef,
    remoteVideoRef,
    localStreamRef,
    socketRef,
    callStartRef,
    peerRef,
    prejoinState,
    customerData,
    BASE_URL,
    SIGNALING_URL,
    setIsMuted,
    setIsVideoOff,
    setRemoteConnected: (val: boolean) => {
      setRemoteConnected(val);
    },
    setIsRecording,
  });

  const { switchCamera, facingMode } = useMediaDevices({
    localVideoRef,
    localStreamRef,
    socketRef,
    peerRef,
    webrtcServiceRef,
    isHost,
    prejoinState,
  });

  const {
    annotations,
    selectedAnnotationTool,
    selectedColor,
    isAnnotating,
    setSelectedAnnotationTool,
    setSelectedColor,
    setIsAnnotating,
    handleAnnotationMouseDown,
    handleAnnotationMouseMove,
    handleAnnotationMouseUp,
    clearAnnotations,
  } = useAnnotations({
    roomId,
    socketRef,
    signalingServiceRef,
    activePanel,
  });

  const {
    chatMessages,
    newMessage,
    hasUnreadMessages,
    setNewMessage,
    setHasUnreadMessages,
    sendMessage,
    handleKeyPress,
  } = useChat({
    roomId,
    isHost,
    socketRef,
    signalingServiceRef,
    activePanel,
    displayName,
  });

  const {
    measurements,
    calibration,
    clearMeasurements,
    guestDeviceModel,
    setGuestDeviceModel,
    fetchCalibration,
  } = useMeasurement({
    roomId,
    isHost,
    socketRef
  });

  // ✅ CRITICAL: Cleanup camera/mic permissions when component unmounts
  useEffect(() => {
    return () => {
      console.log('🧹 [VideoCallRoom] Component unmounting - releasing camera/mic...');

      // Stop all local stream tracks immediately
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => {
          track.stop();
          console.log(`🛑 [Cleanup] Stopped ${track.kind} track`);
        });
        localStreamRef.current = null;
      }

      // Stop remote stream tracks
      if (remoteStreamRef.current) {
        remoteStreamRef.current.getTracks().forEach(track => {
          track.stop();
        });
        remoteStreamRef.current = null;
      }

      // Cleanup WebRTC service
      if (webrtcServiceRef?.current) {
        webrtcServiceRef.current.cleanup();
      }

      // Disconnect socket
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }

      console.log('✅ [Cleanup] Camera/mic permissions released immediately');
    };
  }, []); // Empty deps = runs only on unmount

  // ✅ Sync remote stream from WebRTC service for transcription
  // Uses interval when connected so we pick up remote audio even if it arrives slightly after connection
  useEffect(() => {
    if (!remoteConnected || !webrtcServiceRef?.current) return;
    const sync = () => {
      const remoteStream = webrtcServiceRef.current?.getRemoteStream();
      if (remoteStream && remoteStream !== remoteStreamRef.current) {
        remoteStreamRef.current = remoteStream;
        console.log('🔊 Remote stream updated for transcription');
      }
    };
    sync(); // Run immediately
    const interval = setInterval(sync, 2000); // Re-check every 2s for late-joining tracks
    return () => clearInterval(interval);
  }, [remoteConnected, webrtcServiceRef]);

  // Real-time transcription (no UI, just streaming to backend for AI summary)
  // ✅ ENABLED: Transcribes BOTH parties' audio in real-time for AI summary generation
  // Works independently of recording - AI listens to all calls
  // NOTE: When translation is active, the backend skips Whisper and uses OpenAI Realtime transcription instead
  useRealtimeTranscription({
    roomId,
    socketRef,
    localStreamRef,
    remoteStreamRef, // ✅ Now includes remote audio for complete conversation
    enabled: remoteConnected && !waitingForHost && !isTranslationEnabled // Disable when translation active (OpenAI Realtime handles transcription)
  });

  // Live voice translation — captures local mic audio and streams PCM to backend → OpenAI Realtime API
  // Host sends start-translation when enabled; guest auto-starts streaming when translation-started fires
  const { isTranslating, translationError, clearError } = useTranslation({
    roomId,
    socketRef,
    localStreamRef,
    isHost,
    enabled: isHost && isTranslationEnabled && remoteConnected,
    hostLanguage,
    guestLanguage,
    socketConnected: connectionStatus !== 'idle', // re-registers once socket is live (fires before peer connects)
  });

  // Receive translated audio chunks and play them via AudioContext (gapless scheduling)
  const { isReceivingTranslation } = useTranslationPlayback({
    roomId,
    socketRef,
    socketConnected: connectionStatus !== 'idle', // re-registers once socket is live
  });

  // Sync isTranslationActive: host drives it from isTranslationEnabled; guest gets it from socket events
  useEffect(() => {
    if (isHost) {
      setIsTranslationActive(isTranslationEnabled);
    }
  }, [isHost, isTranslationEnabled]);

  useEffect(() => {
    if (isHost) return;
    const socket = socketRef.current;
    if (!socket) return;
    const onStarted = (data: { roomId: string }) => {
      if (data.roomId !== roomId) return;
      setIsTranslationActive(true);
    };
    const onStopped = (data: { roomId: string }) => {
      if (data.roomId !== roomId) return;
      setIsTranslationActive(false);
    };
    socket.on('translation-started', onStarted);
    socket.on('translation-stopped', onStopped);
    socket.on('translation-error', onStopped);
    return () => {
      socket.off('translation-started', onStarted);
      socket.off('translation-stopped', onStopped);
      socket.off('translation-error', onStopped);
    };
  }, [isHost, roomId, connectionStatus]);

  // Mute WebRTC remote audio as soon as translation is active (don't wait for first chunk)
  // so original audio never plays through while waiting for translated audio to start
  useEffect(() => {
    setRemoteAudioMuted(isTranslationActive);
  }, [isTranslationActive, setRemoteAudioMuted]);

  // Clear guest waiting notification when remote peer connects
  useEffect(() => {
    if (remoteConnected && guestWaiting) {
      setGuestWaiting(false);
    }
  }, [remoteConnected, guestWaiting]);

  // ⏱️ 30-MINUTE CALL TIMER - Starts when both parties are connected
  useEffect(() => {
    if (remoteConnected && connectionStatus === 'connected') {
      // Start 30-minute timer (1800 seconds)
      const CALL_DURATION_SECONDS = 30 * 60; // 30 minutes
      setCallTimeRemaining(CALL_DURATION_SECONDS);
      console.log('⏱️ [CALL TIMER] Starting 30-minute call timer');

      const timerInterval = setInterval(() => {
        setCallTimeRemaining((prev) => {
          if (prev === null) return null;

          const newTime = prev - 1;

          // Log every 5 minutes
          if (newTime % 300 === 0 && newTime > 0) {
            const minutesLeft = Math.floor(newTime / 60);
            console.log(`⏱️ [CALL TIMER] ${minutesLeft} minutes remaining`);
          }

          // Warning at 5 minutes
          if (newTime === 300) {
            console.log('⏱️ [CALL TIMER] 5 minutes remaining');
          }

          // Warning at 1 minute
          if (newTime === 60) {
            console.log('⏱️ [CALL TIMER] 1 minute remaining');
          }

          // Time's up - end call automatically
          if (newTime <= 0) {
            console.log('⏱️ [CALL TIMER] Call time limit reached - ending call');
            // Will trigger handleEndCall through a separate effect
            return -1; // Special value to trigger end call
          }

          return newTime;
        });
      }, 1000);

      return () => {
        console.log('⏱️ [CALL TIMER] Cleaning up timer');
        clearInterval(timerInterval);
      };
    } else {
      // Reset timer if not connected
      setCallTimeRemaining(null);
    }
  }, [remoteConnected, connectionStatus]);


  // Detect mobile view - Use device detection instead of just window width
  // This ensures toolbar stays visible even when device rotates to landscape
  useEffect(() => {
    const checkMobileView = () => {
      // Check if it's a touch device (mobile/tablet)
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      // Check user agent for mobile devices
      const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      // Consider it mobile if it's a touch device OR mobile user agent OR width < 768
      const isMobile = isTouchDevice || isMobileUserAgent || window.innerWidth < 768;
      setIsMobileView(isMobile);
    };

    checkMobileView();
    window.addEventListener("resize", checkMobileView);
    return () => window.removeEventListener("resize", checkMobileView);
  }, []);

  // ✅ Request fullscreen and orientation control - HOST ONLY
  // Guest screen rotates freely (same as host) — no lock or counter-rotation needed
  useEffect(() => {
    if (!isHost) return;

    const enableFullscreenAndOrientation = async () => {
      try {
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

        if (isMobile) {
          console.log('📱 [ORIENTATION] Requesting fullscreen (host)...');

          const elem = document.documentElement;

          try {
            if (elem.requestFullscreen) {
              await elem.requestFullscreen();
            } else if ((elem as any).webkitRequestFullscreen) {
              await (elem as any).webkitRequestFullscreen();
            } else if ((elem as any).mozRequestFullScreen) {
              await (elem as any).mozRequestFullScreen();
            } else if ((elem as any).msRequestFullscreen) {
              await (elem as any).msRequestFullscreen();
            }

            console.log('✅ [ORIENTATION] Fullscreen enabled');

            if ('screen' in window && 'orientation' in window.screen) {
              const screenOrientation = window.screen.orientation as any;
              if ('unlock' in screenOrientation) {
                try {
                  await screenOrientation.unlock();
                  console.log('✅ [ORIENTATION] Orientation unlocked (host)');
                } catch (e: any) {
                  console.warn('⚠️ [ORIENTATION] Could not unlock:', e);
                  if (e.name === 'SecurityError' || e.message?.includes('lock')) {
                    // Orientation unlock not available
                  }
                }
              }
            }
          } catch (fullscreenError: any) {
            console.warn('⚠️ [ORIENTATION] Fullscreen request failed:', fullscreenError.message);
          }
        }
      } catch (error: any) {
        console.warn('⚠️ [ORIENTATION] Setup failed:', error.message);
      }
    };

    const timer = setTimeout(enableFullscreenAndOrientation, 1000);

    return () => {
      clearTimeout(timer);
      try {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {});
        }
        if ('screen' in window && 'orientation' in window.screen) {
          const screenOrientation = window.screen.orientation as any;
          if ('unlock' in screenOrientation) {
            screenOrientation.unlock().catch(() => {});
          }
        }
      } catch (e) {
        // Ignore cleanup errors
      }
    };
  }, [isHost]);

  // ✅ Orientation sync - Both host and guest send orientation for proper video rotation
  // Guest screen rotates freely (like host) — screen.orientation API detects the change
  useOrientationSync({
    socket: socketRef,
    roomId,
    isHost,
    enabled: true,
    facingMode,
  });

  // Recording orientation: VideoStreams updates remoteOrientationRef (single source of truth with live display)

  // Device detection and calibration (for measurements)
  useEffect(() => {
    if (!isHost && selectedDeviceModel) {
      // Guest: send selected device to host
      console.log('📱 Guest device selected:', selectedDeviceModel);

      // Send device info to host via Socket.io
      const socket = socketRef.current;
      if (socket && socket.connected) {
        socket.emit('device-info-response', {
          roomId,
          deviceModel: selectedDeviceModel
        });
      }

      // Listen for device info requests from host
      const handleDeviceInfoRequest = () => {
        socket?.emit('device-info-response', {
          roomId,
          deviceModel: selectedDeviceModel
        });
      };

      socket?.on('request-device-info', handleDeviceInfoRequest);
      return () => {
        socket?.off('request-device-info', handleDeviceInfoRequest);
      };
    }
  }, [isHost, roomId, socketRef, selectedDeviceModel]);

  // Host: request and receive guest device info
  useEffect(() => {
    if (isHost && remoteConnected) {
      // Request guest device info
      console.log('🔍 Host requesting guest device info...');
      socketRef.current?.emit('request-device-info', { roomId });
    }
  }, [isHost, remoteConnected, roomId]);

  // Listen for device info from guest
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !isHost) return;

    const handleDeviceInfo = (payload: { deviceModel: string }) => {
      console.log('✅ Received guest device info:', payload.deviceModel);
      setGuestDeviceModel(payload.deviceModel);
      fetchCalibration(payload.deviceModel);
    };

    socket.on('device-info-response', handleDeviceInfo);
    return () => {
      socket.off('device-info-response', handleDeviceInfo);
    };
  }, [socketRef, isHost, setGuestDeviceModel, fetchCalibration]);

  // Fullscreen removed as per requirements

  // Handle snapshot (with DB tracking)
  // Always capture the guest video:
  // - For host: capture remote video (guest)
  // - For guest: capture local video (themselves)
  const takeSnapshot = async () => {
    const videoEl = isHost ? remoteVideoRef.current : localVideoRef.current;
    if (!videoEl) {
      return;
    }

    if (videoEl.readyState < 2) {
      try {
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(
              new Error(
                "Video not ready yet. Please wait a moment and try again."
              )
            );
          }, 5000);

          videoEl.addEventListener(
            "loadeddata",
            () => {
              clearTimeout(timeout);
              resolve(true);
            },
            { once: true }
          );
        });
      } catch (err: any) {
        return;
      }
    }

    const width = videoEl.videoWidth;
    const height = videoEl.videoHeight;
    if (!width || !height) {
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    try {
      setSavingSnapshot(true);
      console.log('📸 Capturing snapshot...', { width, height, source: videoEl.src });

      ctx.drawImage(videoEl, 0, 0, width, height);

      const blob: Blob = await new Promise((resolve, reject) =>
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("Blob create failed"))),
          "image/png"
        )
      );

      console.log('🖼️ Snapshot blob created:', {
        size: blob.size,
        sizeInKB: (blob.size / 1024).toFixed(2),
        type: blob.type
      });

      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `${roomId}/${isHost ? "host" : "guest"}-${ts}.png`;
      const bucket = "call-snapshots";

      console.log('☁️ Uploading snapshot to Supabase...', { bucket, filename });

      const { error: upErr } = await supabase.storage
        .from(bucket)
        .upload(filename, blob, {
          contentType: "image/png",
          upsert: false,
        });

      if (upErr) {
        console.error('❌ Snapshot upload failed:', upErr);
        throw upErr;
      }

      console.log('✅ Snapshot uploaded to storage successfully');

      // 🔢 Track in call_snapshots table via backend API
      console.log('💾 Saving snapshot metadata via backend API...');
      try {
        const response = await apiClient.post<{ ok: boolean; snapshot: any }>(
          `/session/${roomId}/snapshot`,
          {
            storagePath: filename,
            fileSizeBytes: blob.size,
          }
        );

        if (response.ok) {
          console.log("✅ Snapshot tracked in database:", response.snapshot?.id);
        } else {
          console.warn("❌ Failed to track snapshot in database");
        }
      } catch (snapshotErr: any) {
        console.warn("❌ Could not track snapshot in database:", snapshotErr.message);
        // Don't fail the overall operation - snapshot was still uploaded to storage
      }

      console.log("✅ Snapshot saved successfully");
    } catch (e: any) {
    } finally {
      setSavingSnapshot(false);
    }
  };

  // 🔚 handleEndCall that hits /update-call-session
  const handleEndCall = useCallback(async () => {
    // ✅ FIX: Prevent double-press of end call button
    if (isEndingCall) {
      console.log('⏭️ End call already in progress, ignoring duplicate press');
      return;
    }
    setIsEndingCall(true);
    console.log('🔴 END CALL INITIATED - UI locked');

    // ✅ FIX: Release camera/mic permissions IMMEDIATELY when call ends
    if (localStreamRef.current) {
      console.log('🛑 Releasing camera/mic permissions...');
      localStreamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log(`🛑 Stopped ${track.kind} track`);
      });
      localStreamRef.current = null;
      console.log('✅ Camera/mic released');
    }

    // ✅ FIX: Wait for recording upload to complete before navigating away
    if (isRecording) {
      console.log('⏸️ Stopping recording and waiting for upload to complete...');
      await stopRecording();
      // Only needed for backend-side mediasoup recorder path.
      if (USE_MEDIASOUP_SERVER_RECORDING) {
        await new Promise((r) => setTimeout(r, 800));
      }
      console.log('✅ Recording stopped and upload complete');
    }

    // If host, emit end-call event to force all participants to leave
    if (isHost && socketRef.current) {
      socketRef.current.emit("end-call", { roomId });
      console.log('🔴 Host ending call for all participants');
    }

    await webrtcEndCall();

    const end = new Date();
    const start = callStartRef.current || end;
    const duration = Math.max(
      Math.round((end.getTime() - start.getTime()) / 60000),
      1
    );

    // ✅ FIX: Only mark call as 'ended' if HOST is ending it
    // Guests leaving should NOT prevent rejoin within 30-minute window
    if (isHost) {
      // ✅ SAVE NOTES: Save notes to backend before ending call
      if (notes && notes.trim()) {
        try {
          console.log('📝 Saving notes before ending call...');
          await apiClient.patch(`/sessions/${roomId}/notes`, { notes: notes.trim() });
          console.log('✅ Notes saved successfully');
        } catch (notesError) {
          console.error('❌ Failed to save notes:', notesError);
          // Don't block call end if notes fail to save
        }
      }

      // Update call session status via backend - mark as 'ended'
      try {
        // ✅ SECURITY: Include auth token for host verification
        const { data: { session } } = await supabase.auth.getSession();
        const headers: HeadersInit = { "Content-Type": "application/json" };
        if (session?.access_token) {
          headers["Authorization"] = `Bearer ${session.access_token}`;
        }

        const response = await fetch(`${BASE_URL}/api/update-call-session`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            roomId,
            event: "call-ended",
            participantType: "host",
          }),
        });

        const data = await response.json();

        if (!data.ok) {
          console.error("Failed to update call session on end:", data.error);
        } else {
          console.log(`✅ Call session ended by HOST - marked as 'ended', rejoin disabled`);

          // Show AI summary modal for host (always show if transcription was running)
          setTimeout(() => {
            setShowAISummary(true);
            console.log('🤖 Opening AI summary modal...');
          }, 1500);
        }
      } catch (error) {
        console.error("Error updating call session on end:", error);
      }
    } else {
      // ✅ Guest leaving - do NOT mark call as ended
      console.log('🚪 Guest leaving call - NOT marking as ended (rejoin allowed within 30-min window)');
    }

    const callEndData = {
      roomId,
      durationMinutes: duration,
      callStartTime: start.toISOString(),
      callEndTime: end.toISOString(),
      notes,
      isHost,
    };

    // Notify React Native WebView if applicable
    if ((window as any).ReactNativeWebView) {
      try {
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify({
            type: "call-ended",
            data: callEndData,
          })
        );
      } catch (e) {
        console.error("Error posting message to React Native:", e);
      }
    }

    // Dispatch custom event
    try {
      window.dispatchEvent(
        new CustomEvent("call-ended", {
          detail: callEndData,
        })
      );
    } catch (e) {
      console.error("Error dispatching call-ended event:", e);
    }

    onEndCall(callEndData);
  }, [isRecording, isHost, roomId, webrtcEndCall, BASE_URL, notes, onEndCall, stopRecording, isEndingCall]);

  // ⏱️ Watch for 30-minute timer expiration and end call
  useEffect(() => {
    if (callTimeRemaining === -1) {
      console.log('⏱️ [CALL TIMER] Timer expired - triggering end call');
      setTimeout(() => {
        handleEndCall();
      }, 2000);
    }
  }, [callTimeRemaining, handleEndCall]);

  // Guest measurement handlers
  const startGuestMeasurement = () => {
    console.log('📏 Guest starting measurement');

    // Step 1: Show device selection if not already selected
    if (!selectedDeviceModel) {
      setShowDeviceSelection(true);
      return;
    }

    // Step 2: Show reference object selector if not calibrated
    if (!depthScale) {
      setShowReferenceObjectSelector(true);
      return;
    }

    // Step 3: Start actual measurement
    setIsGuestMeasuring(true);
    setGuestMeasurementPoints([]);
  };

  const cancelGuestMeasurement = () => {
    console.log('❌ Guest cancelled measurement');
    setIsGuestMeasuring(false);
    setGuestMeasurementPoints([]);
    setCurrentMeasurementResult(null);
  };

  const undoLastMeasurementPoint = () => {
    setGuestMeasurementPoints(prev => {
      const newPoints = prev.slice(0, -1);
      console.log(`↩️ Undo: removed last point, ${newPoints.length} remaining`);
      return newPoints;
    });
  };

  const deleteCurrentMeasurement = () => {
    console.log('🗑️ Deleting current measurement');
    setIsGuestMeasuring(false);
    setGuestMeasurementPoints([]);
    setCurrentMeasurementResult(null);
  };

  // Reference calibration handlers
  const handleReferenceObjectSelected = (objectType: string, lengthInches: number) => {
    console.log(`📐 Reference object selected: ${objectType} (${lengthInches}")`);
    setReferenceObject({ name: objectType, expectedLength: lengthInches });
    setShowReferenceObjectSelector(false);
    setIsReferenceCalibrating(true);
    setReferencePoints([]);
  };

  const handleReferenceCalibrationClick = async (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isReferenceCalibrating || !localVideoRef.current || !referenceObject) return;

    const video = localVideoRef.current;
    const rect = e.currentTarget.getBoundingClientRect();

    // Calculate click position (same logic as measurement)
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    const videoAspectRatio = video.videoWidth / video.videoHeight;
    const containerAspectRatio = rect.width / rect.height;

    let videoDisplayWidth = rect.width;
    let videoDisplayHeight = rect.height;
    let videoOffsetX = 0;
    let videoOffsetY = 0;

    if (containerAspectRatio > videoAspectRatio) {
      videoDisplayWidth = rect.height * videoAspectRatio;
      videoOffsetX = (rect.width - videoDisplayWidth) / 2;
    } else {
      videoDisplayHeight = rect.width / videoAspectRatio;
      videoOffsetY = (rect.height - videoDisplayHeight) / 2;
    }

    const relativeX = clickX - videoOffsetX;
    const relativeY = clickY - videoOffsetY;

    if (relativeX < 0 || relativeX > videoDisplayWidth || relativeY < 0 || relativeY > videoDisplayHeight) {
      console.log('Click outside video bounds');
      return;
    }

    const x = (relativeX / videoDisplayWidth) * 100;
    const y = (relativeY / videoDisplayHeight) * 100;

    const newPoints = [...referencePoints, { x, y }];
    setReferencePoints(newPoints);

    console.log(`📍 Reference point ${newPoints.length}: (${x.toFixed(1)}%, ${y.toFixed(1)}%)`);

    if (newPoints.length === 2) {
      // Calculate depth scale from reference object
      await processReferenceCalibration(newPoints[0], newPoints[1]);
    }
  };

  const processReferenceCalibration = async (
    point1: { x: number; y: number },
    point2: { x: number; y: number }
  ) => {
    try {
      if (!referenceObject) return;

      const video = localVideoRef.current;
      if (!video || video.readyState < 2) {
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return;
      }

      ctx.drawImage(video, 0, 0);
      const frameData = canvas.toDataURL('image/jpeg', 0.8);

      const deviceModel = selectedDeviceModel || detectDevice().modelIdentifier;

      console.log('📐 Processing reference calibration...');

      // Send to backend to get measurement
      const response = await fetch(`${BASE_URL}/api/measurement/point-to-point`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          frameData,
          point1,
          point2,
          deviceModel,
          roomId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.hint || errorData.details || `Calibration failed: ${response.status}`;
        throw new Error(errorMsg);
      }

      const data = await response.json();
      const measuredDistance = data.measurement.real_distance_inches;

      // Calculate scale factor
      const scale = referenceObject.expectedLength / measuredDistance;
      setDepthScale(scale);

      console.log(`✅ Calibration complete!`);
      console.log(`   Expected: ${referenceObject.expectedLength}"`);
      console.log(`   Measured: ${measuredDistance.toFixed(2)}"`);
      console.log(`   Scale factor: ${scale.toFixed(3)}`);

      // Reset calibration state and start measurement
      setIsReferenceCalibrating(false);
      setReferencePoints([]);
      setIsGuestMeasuring(true);
      setGuestMeasurementPoints([]);

    } catch (error: any) {
      console.error('❌ Reference calibration failed:', error);
      setIsReferenceCalibrating(false);
      setReferencePoints([]);
    }
  };

  const cancelReferenceCalibration = () => {
    console.log('❌ Cancelled reference calibration');
    setIsReferenceCalibrating(false);
    setReferencePoints([]);
  };

  const undoReferencePoint = () => {
    setReferencePoints(prev => {
      const newPoints = prev.slice(0, -1);
      console.log(`↩️ Undo: removed reference point, ${newPoints.length} remaining`);
      return newPoints;
    });
  };

  const handleGuestMeasurementClick = async (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isGuestMeasuring || !localVideoRef.current) return;

    const video = localVideoRef.current;
    const rect = e.currentTarget.getBoundingClientRect();

    // Calculate click position relative to the container
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Get actual video dimensions and position within container
    const videoAspectRatio = video.videoWidth / video.videoHeight;
    const containerAspectRatio = rect.width / rect.height;

    let videoDisplayWidth = rect.width;
    let videoDisplayHeight = rect.height;
    let videoOffsetX = 0;
    let videoOffsetY = 0;

    // Video is letterboxed (black bars on top/bottom)
    if (containerAspectRatio > videoAspectRatio) {
      videoDisplayWidth = rect.height * videoAspectRatio;
      videoOffsetX = (rect.width - videoDisplayWidth) / 2;
    }
    // Video is pillarboxed (black bars on left/right)
    else if (containerAspectRatio < videoAspectRatio) {
      videoDisplayHeight = rect.width / videoAspectRatio;
      videoOffsetY = (rect.height - videoDisplayHeight) / 2;
    }

    // Calculate position relative to actual video content (not black bars)
    const videoX = clickX - videoOffsetX;
    const videoY = clickY - videoOffsetY;

    // Convert to percentage of actual video area
    const x = (videoX / videoDisplayWidth) * 100;
    const y = (videoY / videoDisplayHeight) * 100;

    // Ignore clicks outside the actual video area
    if (x < 0 || x > 100 || y < 0 || y > 100) {
      console.log('⚠️ Click outside video area, ignoring');
      return;
    }

    const newPoints = [...guestMeasurementPoints, { x, y }];
    setGuestMeasurementPoints(newPoints);

    console.log(`📍 Guest point ${newPoints.length}: (${x.toFixed(1)}%, ${y.toFixed(1)}%)`);

    if (newPoints.length === 2) {
      // Capture frame and send to backend
      await processGuestMeasurement(newPoints[0], newPoints[1]);
    }
  };

  const processGuestMeasurement = async (
    point1: { x: number; y: number },
    point2: { x: number; y: number }
  ) => {
    try {
      // Capture frame from guest's LOCAL video
      const video = localVideoRef.current;
      if (!video || video.readyState < 2) {
        setIsGuestMeasuring(false);
        setGuestMeasurementPoints([]);
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        setIsGuestMeasuring(false);
        setGuestMeasurementPoints([]);
        return;
      }

      ctx.drawImage(video, 0, 0);
      const frameData = canvas.toDataURL('image/jpeg', 0.8);

      // Use selected device model or fallback to detection
      const deviceModel = selectedDeviceModel || detectDevice().modelIdentifier;

      console.log('📏 Processing guest measurement with device:', deviceModel);

      // Send to backend
      const response = await fetch(`${BASE_URL}/api/measurement/point-to-point`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          frameData,
          point1,
          point2,
          deviceModel,
          roomId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.hint || errorData.details || `Measurement API failed: ${response.status}`;
        throw new Error(errorMsg);
      }

      const data = await response.json();

      // Apply depth scale correction if calibrated
      let correctedMeasurement = { ...data.measurement };
      if (depthScale) {
        correctedMeasurement.real_distance_inches = data.measurement.real_distance_inches * depthScale;
        correctedMeasurement.real_distance_cm = data.measurement.real_distance_cm * depthScale;
        console.log(`📐 Applied depth scale ${depthScale.toFixed(3)}x: ${data.measurement.real_distance_inches.toFixed(2)}" → ${correctedMeasurement.real_distance_inches.toFixed(2)}"`);
      }

      const measurement = {
        id: Date.now().toString(),
        type: 'point_to_point',
        timestamp: new Date(),
        point1,
        point2,
        initiatedBy: 'guest' as const,
        deviceModel,
        ...correctedMeasurement
      };

      // Broadcast to both host and guest
      socketRef.current?.emit('measurement-result', {
        roomId,
        measurement
      });

      console.log(`✅ Measurement complete: ${measurement.real_distance_inches?.toFixed(2)}" (${measurement.real_distance_cm?.toFixed(2)}cm)`);

      // Store measurement result and keep overlay visible
      setCurrentMeasurementResult({
        real_distance_cm: measurement.real_distance_cm,
        real_distance_inches: measurement.real_distance_inches,
        confidence: measurement.confidence
      });
    } catch (error: any) {
      console.error('❌ Guest measurement failed:', error);
      setIsGuestMeasuring(false);
      setGuestMeasurementPoints([]);
    }
  };

  // ✅ Wrapper: block annotations until remote user is connected
  const handleAnnotationMouseDownGuard = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (!remoteConnected) {
      return;
    }
    handleAnnotationMouseDown(e);
  };

  const handleAnnotationMouseMoveGuard = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (!remoteConnected) return;
    handleAnnotationMouseMove(e);
  };

  const handleAnnotationMouseUpGuard = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (!remoteConnected) return;
    handleAnnotationMouseUp(e);
  };

  // Close panels on outside click - BUT keep annotate panel sticky
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Chat panel handling
      const isOutsideChat =
        !target.closest('[data-panel="chat"]') &&
        !target.closest('[data-panel-trigger="chat"]');

      // Notes panel handling
      const isOutsideNotes = isHost
        ? !target.closest('[data-panel="notes"]') &&
          !target.closest('[data-panel-trigger="notes"]')
        : true;

      // Mobile menu handling
      const isOutsideMobileMenu =
        !target.closest("[data-mobile-menu]") &&
        !target.closest("[data-mobile-menu-trigger]");

      // Close chat if clicked outside
      if (isOutsideChat && activePanel === "chat") {
        setActivePanel(null);
      }

      // Close notes if clicked outside
      if (isOutsideNotes && activePanel === "notes") {
        setActivePanel(null);
      }

      // ❌ Do NOT auto-close annotate panel here.
      // It will only close when the user explicitly closes it
      // via the toolbar button or the panel close/delete buttons.

      // Close mobile menu if clicked outside
      if (isOutsideMobileMenu && showMobileMenu) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activePanel, showMobileMenu, isHost]);

  // ✅ FIX: Check if a recording was interrupted (e.g. by page refresh)
  useEffect(() => {
    if (!isHost) return;
    const wasInterrupted = sessionStorage.getItem(`recording_interrupted_${roomId}`);
    if (wasInterrupted) {
      console.log('⚠️ [Recording] Previous recording was interrupted by page refresh');
      // Clear the flags
      sessionStorage.removeItem(`recording_interrupted_${roomId}`);
    }
  }, [isHost, roomId]);

  // Auto-start recording when guest joins (host only).
  // Prejoin toggle can still explicitly disable it.
  const hasAutoStartedRecording = useRef(false);
  useEffect(() => {
    if (!isHost || !remoteConnected || isRecording || hasAutoStartedRecording.current) return;

    // Default to auto-start on guest join unless prejoin explicitly disabled it.
    const autoStart = prejoinState?.autoStartRecording ?? true;
    if (autoStart) {
      console.log('🎥 ========================================');
      console.log('🎥 AUTO-START RECORDING TRIGGERED');
      console.log('🎥 ========================================');
      console.log('🎥 Prejoin state:', prejoinState);
      console.log('🎥 autoStartRecording:', autoStart);
      console.log('🎥 isHost:', isHost);
      console.log('🎥 remoteConnected:', remoteConnected);
      console.log('🎥 isRecording:', isRecording);
      hasAutoStartedRecording.current = true;

      // ✅ IMPROVED: Check if remote video is ready before starting
      const timer = setTimeout(async () => {
        console.log('🎥 3-second delay complete, checking remote video readiness...');
        console.log('🎥 Remote video element:', {
          exists: !!remoteVideoRef.current,
          hasSrcObject: !!remoteVideoRef.current?.srcObject,
          readyState: remoteVideoRef.current?.readyState,
          videoWidth: remoteVideoRef.current?.videoWidth,
          videoHeight: remoteVideoRef.current?.videoHeight,
        });

        // Verify remote video is ready
        if (!remoteVideoRef.current?.srcObject) {
          console.warn('⚠️ Remote video not ready, delaying auto-record by 2 more seconds...');
          setTimeout(() => {
            console.log('✅ 5-second total delay - initiating delayed auto-recording');
            startRecording();
          }, 2000);
          return;
        }

        console.log('✅ ========================================');
        console.log('✅ INITIATING AUTO-RECORDING NOW');
        console.log('✅ ========================================');
        startRecording();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isHost, remoteConnected, prejoinState, isRecording, startRecording]);

  // ✅ Recording state changes are now handled in useWebRTC.ts via SignalingService
  // This ensures the listener is set up before socket events can arrive (no race condition)

  // Keep recording alive across guest disconnects/rejoins.
  // Recording is finalized only when host explicitly ends the call.

  // ✅ CRITICAL FIX: Listen for 'call-ended' (not 'call-ended-by-host')
  // Backend emits 'call-ended' when host ends the call
  // Note: peer-left is already handled by useWebRTC/SignalingService
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const handleCallEndedByHost = () => {
      console.log('📢 [Socket] Received call-ended event', { isHost });

      if (isHost) {
        // Host ended their own call, just end it
        setTimeout(() => {
          handleEndCall();
        }, 1000);
      } else {
        // Guest received host-ended notification - start 10 second countdown
        console.log('⏰ [Guest] Host ended call, starting 10s countdown');

        // ✅ FIX: Release camera/mic permissions immediately for guest
        if (localStreamRef.current) {
          console.log('🛑 [Guest] Releasing camera/mic permissions...');
          localStreamRef.current.getTracks().forEach(track => {
            track.stop();
            console.log(`🛑 Stopped ${track.kind} track for guest`);
          });
          localStreamRef.current = null;
        }

        setHostLeftCountdown(10);
      }
    };

    socket.on("call-ended", handleCallEndedByHost);

    return () => {
      socket.off("call-ended", handleCallEndedByHost);
    };
  }, [socketRef, handleEndCall, isHost]);

  // Listen for guest waiting notification (host only)
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !isHost) return;

    const handleGuestWaiting = (data: { guestSocketId: string; roomId: string }) => {
      console.log('👋 Guest is waiting to join:', data);
      setGuestWaiting(true);

      // Auto-hide notification after guest connects
      setTimeout(() => setGuestWaiting(false), 5000);
    };

    socket.on("guest-waiting", handleGuestWaiting);

    return () => {
      socket.off("guest-waiting", handleGuestWaiting);
    };
  }, [socketRef, isHost]);

  // No longer need to listen for measurement requests - guest initiates directly via toolbar button

  // Debug: Log waitingForHost state changes
  useEffect(() => {
    console.log('🔍 DEBUG VideoCallRoom: waitingForHost changed to:', waitingForHost);
  }, [waitingForHost]);

  // Host-left countdown timer
  useEffect(() => {
    if (hostLeftCountdown === null || hostLeftCountdown <= 0) return;

    const timer = setInterval(() => {
      setHostLeftCountdown(prev => {
        if (prev === null || prev <= 1) {
          // Countdown reached 0, end call automatically
          handleEndCall();
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [hostLeftCountdown, handleEndCall]);

  // Auto-dismiss rotation lock prompt after 5 seconds (host only)
  useEffect(() => {
    if (rotationLockSuspected && isHost && !rotationPromptDismissed) {
      const dismissTimer = setTimeout(() => {
        console.log('[VideoCallRoom] Auto-dismissing rotation lock prompt after 5 seconds');
        setRotationPromptDismissed(true);
      }, 5000);

      return () => clearTimeout(dismissTimer);
    }
  }, [rotationLockSuspected, isHost, rotationPromptDismissed]);

  return (
    <div
      className="fixed inset-0 bg-black text-white overflow-hidden"
    >
      <div
        className="flex flex-col bg-black text-white h-full w-full"
      >
      <VideoCallToasts />

      {/* Guest Waiting for Host Screen */}
      {waitingForHost && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/95 backdrop-blur-sm cursor-pointer"
          onClick={async () => {
            // User tap will trigger the startCall event listeners AND force videos to play
            console.log('👆 User tapped waiting overlay - forcing video playback');

            // ✅ iOS Audio Fix: handleTapToPlay resumes AudioContext + plays videos
            await handleTapToPlay();

            // Force play all video elements to overcome autoplay restrictions
            const playVideo = async (videoRef: React.RefObject<HTMLVideoElement>, name: string) => {
              if (videoRef.current) {
                try {
                  await videoRef.current.play();
                  console.log(`✅ ${name} video forced to play`);
                } catch (e) {
                  console.warn(`⚠️ ${name} video play failed:`, e);
                }
              }
            };

            await playVideo(localVideoRef, 'Local');
            await playVideo(remoteVideoRef, 'Remote');
          }}
        >
          <div className="text-center px-6 py-8 max-w-md pointer-events-none">
            <div className="w-20 h-20 mx-auto mb-6 relative">
              <div className="absolute inset-0 border-4 border-white/20 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-t-white border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
            </div>
            <h2 className="text-2xl font-bold mb-3">Waiting for host to join...</h2>
            <p className="text-gray-400 text-sm">
              The host will start the call shortly. Please wait.
            </p>
            <p className="text-gray-500 text-xs mt-4 animate-pulse">
              Tap anywhere to prepare your device
            </p>
          </div>
        </div>
      )}

      {/* Guest waiting notification - Host only */}
      {isHost && guestWaiting && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-blue-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-pulse">
            <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
            <span className="font-medium">Guest is joining the call...</span>
          </div>
        </div>
      )}

      {/* Rotation Lock Prompt - Host only: ask to unlock device rotation for best experience */}
      {rotationLockSuspected && isHost && !rotationPromptDismissed && (
        <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-sm mx-4">
          <div className="bg-yellow-500/95 backdrop-blur-sm text-black px-6 py-4 rounded-lg shadow-2xl relative">
            {/* Close button */}
            <button
              onClick={() => setRotationPromptDismissed(true)}
              className="absolute top-2 right-2 text-black/60 hover:text-black transition-colors"
              aria-label="Dismiss"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="flex items-start gap-3 pr-6">
              <svg className="w-6 h-6 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="font-semibold text-sm mb-1">Unlock Screen Rotation</p>
                <p className="text-xs opacity-90">
                  For the best experience, please disable rotation lock in your device settings or control center.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recording Indicator - Shows for all participants when call is being recorded */}
      {isRecording && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-red-500/90 backdrop-blur-sm text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
            <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
            <span className="font-medium text-sm">Recording</span>
          </div>
        </div>
      )}

      {/* Live Translation Badge - shown to both parties when translation is active */}
      {isReceivingTranslation && (
        <div className={`absolute z-50 ${isRecording ? 'top-16' : 'top-4'} left-1/2 transform -translate-x-1/2`}>
          <div className="bg-blue-500/90 backdrop-blur-sm text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m5 8 6 6"/><path d="m4 14 6-6 2-3"/><path d="M2 5h12"/><path d="M7 2h1"/><path d="m22 22-5-10-5 10"/><path d="M14 18h6"/>
            </svg>
            <span className="font-medium text-sm">Live Translation Active</span>
            {isTranslating && <div className="w-2 h-2 bg-white rounded-full animate-pulse" />}
          </div>
        </div>
      )}

      {/* Translation error toast — auto-dismissable */}
      {translationError && (
        <div className="absolute top-4 right-4 z-50 max-w-xs">
          <div className="bg-yellow-500/90 backdrop-blur-sm text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
            <span className="text-sm">{translationError}</span>
            <button onClick={clearError} className="ml-2 text-white/70 hover:text-white text-lg leading-none">&times;</button>
          </div>
        </div>
      )}

      <VideoCallToolbar
        isMobileView={isMobileView}
        isHost={isHost}
        isMuted={isMuted}
        isVideoOff={isVideoOff}
        isRecording={isRecording}

        activePanel={activePanel}
        hasUnreadMessages={hasUnreadMessages}
        savingSnapshot={savingSnapshot}
        showMobileMenu={showMobileMenu}
        setShowMobileMenu={setShowMobileMenu}
        setActivePanel={setActivePanel}
        setHasUnreadMessages={setHasUnreadMessages}
        toggleMute={toggleMute}
        toggleVideo={toggleVideo}
        toggleRecording={toggleRecording}
        takeSnapshot={takeSnapshot}
        switchCamera={switchCamera}
        handleEndCall={handleEndCall}
        setIsAnnotating={setIsAnnotating}
        setSelectedAnnotationTool={setSelectedAnnotationTool}
        startGuestMeasurement={!isHost ? startGuestMeasurement : undefined}
        isGuestMeasuring={!isHost ? isGuestMeasuring : false}
        isTranslationEnabled={isTranslationEnabled}
        isTranslating={isTranslating}
        hostLanguage={hostLanguage}
        guestLanguage={guestLanguage}
        toggleTranslation={() => setIsTranslationEnabled(false)}
        onStartTranslation={(hLang, gLang) => {
          setHostLanguage(hLang);
          setGuestLanguage(gLang);
          setIsTranslationEnabled(true);
        }}
      />

      <div className="flex-1 flex relative">
        <VideoStreams
          localVideoRef={localVideoRef}
          remoteVideoRef={remoteVideoRef}
          remoteConnected={remoteConnected}
          connectionStatus={connectionStatus}
          roomId={roomId}
          isHost={isHost}
          isChatOpen={isMobileView && (activePanel === "chat" || activePanel === "notes")}
          isMobileView={isMobileView}
          participantName={customerData?.name || participantName}
          guestName={customerData?.name || "Guest"}
          annotations={annotations}
          isAnnotating={isAnnotating}
          selectedAnnotationTool={selectedAnnotationTool}
          handleAnnotationMouseDown={handleAnnotationMouseDownGuard}
          handleAnnotationMouseMove={handleAnnotationMouseMoveGuard}
          handleAnnotationMouseUp={handleAnnotationMouseUpGuard}
          isVideoOff={isVideoOff}
          remoteVideoEnabled={remoteVideoEnabled}
          remoteVideoMuted={remoteVideoMuted}
          measurements={measurements}
          facingMode={facingMode}
          socket={socketRef}
          remoteOrientationRef={isHost ? remoteOrientationRef : undefined}
        />

        {activePanel === "chat" && (
          <ChatPanel
            isMobileView={isMobileView}
            chatMessages={chatMessages}
            newMessage={newMessage}
            setNewMessage={setNewMessage}
            setActivePanel={setActivePanel}
            sendMessage={sendMessage}
            handleKeyPress={handleKeyPress}
            isHost={isHost}
            currentSocketId={socketRef.current?.id}
          />
        )}

        {isHost && activePanel === "notes" && (
          <NotesPanel
            isMobileView={isMobileView}
            notes={notes}
            setNotes={setNotes}
            setActivePanel={setActivePanel}
          />
        )}

        {isHost && activePanel === "annotate" && (
          <AnnotatePanel
            isMobileView={isMobileView}
            selectedAnnotationTool={selectedAnnotationTool}
            selectedColor={selectedColor}
            setSelectedAnnotationTool={setSelectedAnnotationTool}
            setSelectedColor={setSelectedColor}
            setActivePanel={setActivePanel}
            setIsAnnotating={setIsAnnotating}
            clearAnnotations={clearAnnotations}
            handleAnnotationToolSelect={(tool) => {
              setSelectedAnnotationTool(tool);
              setIsAnnotating(true);
            }}
          />
        )}

        {isHost && activePanel === "measure" && (
          <MeasurePanel
            isMobileView={isMobileView}
            measurements={measurements}
            calibration={calibration}
            setActivePanel={setActivePanel}
            clearMeasurements={clearMeasurements}
          />
        )}

      </div>

      {/* Guest Measurement Overlay - GUEST ONLY */}
      {!isHost && isGuestMeasuring && (
        <GuestMeasurementOverlay
          points={guestMeasurementPoints}
          onVideoClick={handleGuestMeasurementClick}
          onCancel={cancelGuestMeasurement}
          onUndo={undoLastMeasurementPoint}
          measurementResult={currentMeasurementResult}
          onDeleteMeasurement={deleteCurrentMeasurement}
        />
      )}

      {/* Guest AR Measurement Overlay - HOST ONLY */}
      {isHost && (
        <GuestARMeasurementOverlay
          socketRef={socketRef}
          roomId={roomId}
        />
      )}

      {/* Host Left Modal - 10 second countdown */}
      {hostLeftCountdown !== null && hostLeftCountdown > 0 && (
        <HostLeftModal
          countdown={hostLeftCountdown}
          onStay={() => {
            // Guest wants to stay - cancel countdown
            setHostLeftCountdown(null);
          }}
          onLeaveNow={() => {
            // Guest wants to leave immediately
            setHostLeftCountdown(null);
            handleEndCall();
          }}
        />
      )}

      {/* Download Guest App Modal - Guest Only */}
      {!isHost && showDeviceSelection && (
        <DownloadGuestAppModal
          onClose={() => {
            setShowDeviceSelection(false);
            console.log('📱 Guest app download prompt closed');
          }}
        />
      )}

      {/* Reference Object Selector - Guest Only */}
      {!isHost && showReferenceObjectSelector && (
        <ReferenceObjectSelector onObjectSelected={handleReferenceObjectSelected} />
      )}

      {/* Reference Calibration Overlay - Guest Only */}
      {!isHost && isReferenceCalibrating && referenceObject && (
        <ReferenceCalibrationOverlay
          points={referencePoints}
          objectName={referenceObject.name}
          expectedLength={referenceObject.expectedLength}
          onVideoClick={handleReferenceCalibrationClick}
          onCancel={cancelReferenceCalibration}
          onUndo={undoReferencePoint}
        />
      )}

      {/* AI Summary Modal - Host Only, shown after call ends */}
      {isHost && showAISummary && (
        <AISummaryModal
          roomId={roomId}
          baseUrl={BASE_URL}
          onClose={() => setShowAISummary(false)}
        />
      )}

      {/* Upload Progress Modal - Centered overlay during recording upload */}
      {(uploadProgress !== null || isEndingCall) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: 16,
          }}
        >
          <div
            style={{
              backgroundColor: '#000000',
              borderRadius: 12,
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 16,
              width: 280,
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            {/* Spinner or checkmark */}
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: uploadProgress === 100 ? '#94ACF8' : 'transparent',
                border: uploadProgress === 100 ? 'none' : '3px solid rgba(255, 255, 255, 0.1)',
                borderTopColor: uploadProgress === 100 ? 'transparent' : '#94ACF8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: uploadProgress === 100 ? 'none' : 'spin 1s linear infinite',
              }}
            >
              {uploadProgress === 100 && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              )}
            </div>

            {/* Title */}
            <div style={{ color: '#fff', fontSize: 16, fontWeight: 600, textAlign: 'center' }}>
              {uploadProgress === null
                ? 'Ending Call...'
                : uploadProgress < 100
                  ? 'Uploading Recording'
                  : 'Upload Complete!'}
            </div>

            {/* Progress bar - only show when actually uploading */}
            {uploadProgress !== null && (
              <>
                <div
                  style={{
                    width: '100%',
                    height: 6,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 3,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${uploadProgress}%`,
                      backgroundColor: '#94ACF8',
                      borderRadius: 3,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
                <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 14 }}>
                  {uploadProgress}%
                </div>
              </>
            )}

            {/* Subtitle */}
            <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 12, textAlign: 'center', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
              {uploadProgress === null
                ? 'Please wait...'
                : uploadProgress < 100
                  ? 'Do not close or reload this page.\nYour recording will be lost.'
                  : 'Redirecting...'}
            </div>
          </div>

          {/* CSS for spinner animation */}
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      )}
      </div>
    </div>
  );
}
